import { Box, Link as MuiLink, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useFeatureFlagsContext } from '../../contexts/featureFlagContext';
import { DoorVisitTableFooter } from '../DoorVisitTableFooter';
import { buildVoterDetailPath, type StoredVotersListState } from './voterNavigation';
import { NO_HOUSEHOLD_LINKED, VOTER_PAGE_SIZE_OPTIONS, displayField, displayVoterName, formatHouseholdAddress } from './voterPresentation';
import type { VoterListItem } from './useVoterList';

const identifierSx = { overflowWrap: 'anywhere', whiteSpace: 'normal', wordBreak: 'break-word' } as const;

export type VoterTableProps = {
  items: readonly VoterListItem[];
  total: number;
  page: number;
  rowsPerPage: number;
  linkState: { votersList: StoredVotersListState };
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

const householdLabel = (item: VoterListItem): string => {
  if (item.household === null) return NO_HOUSEHOLD_LINKED;
  const lines = formatHouseholdAddress(item.household);
  return lines.length > 0 ? lines.join(', ') : displayField('');
};

export const VoterTable = ({
  items,
  total,
  page,
  rowsPerPage,
  linkState,
  onPageChange,
  onRowsPerPageChange,
}: VoterTableProps) => {
  const { isStructurelyAdmin } = useFeatureFlagsContext();

  return (
    <Box data-testid="voter-table">
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 720 }} aria-label="Voters">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {isStructurelyAdmin ? <TableCell>Voter ID</TableCell> : null}
              <TableCell>State</TableCell>
              <TableCell>Household</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => {
              const name = displayVoterName(item);
              const detailPath = buildVoterDetailPath(item.id);
              return (
                <TableRow key={item.id} hover>
                  <TableCell sx={identifierSx}>
                    {detailPath === null ? (
                      name
                    ) : (
                      <MuiLink component={RouterLink} to={detailPath} state={linkState} sx={identifierSx}>
                        {name}
                      </MuiLink>
                    )}
                  </TableCell>
                  {isStructurelyAdmin ? <TableCell sx={identifierSx}>{displayField(item.voterId)}</TableCell> : null}
                  <TableCell sx={identifierSx}>{displayField(item.state)}</TableCell>
                  <TableCell sx={identifierSx}>{householdLabel(item)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <DoorVisitTableFooter
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[...VOTER_PAGE_SIZE_OPTIONS]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  );
};
